import { useRef } from "react";
import { Color, Flex } from "src/elements";
import { Icon } from "src/elements/Icon/Icon";
import { useHoverClickable } from "src/utils/hoverClickable";
import styled from "styled-components";

const Arrows = styled(Flex)`
  position: absolute;
  top: 0;
  right: 0;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  height: 100%;
  width: 18px;
  padding: 2px;
`;

const ArrowBox = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  user-select: none;

  ${({ isDisabled }) =>
    !isDisabled &&
    `
  &:hover {
    background-color: ${Color.primaryHover};
  }

  &:active {
    background-color: ${Color.primaryActive};

  }
  `}
`;

interface Props {
  value: number;
  setValue: (value: number) => void;
  isDisabled: boolean;
  min?: number;
  max?: number;
  /** How far one click moves the value. */
  step?: number;
}

// A fractional step must not leave float dust behind: 0.25 + 0.05 is 0.30000000000000004.
const roundStep = (value: number) => Math.round(value * 1e6) / 1e6;

export const NumberArrows = ({
  value,
  setValue,
  isDisabled: _isDisabled,
  min,
  max,
  step = 1,
}: Props) => {
  const refUp = useRef<HTMLDivElement>(null);
  const refDown = useRef<HTMLDivElement>(null);

  const { isHovered: isHoveredUp } = useHoverClickable(refUp);
  const { isHovered: isHoveredDown } = useHoverClickable(refDown);

  const isMinArrowDisabled = _isDisabled || (min !== undefined && roundStep(value - step) < min);

  const isMaxArrowDisabled = _isDisabled || (max !== undefined && roundStep(value + step) > max);

  const onIncrease = () => {
    if (isMaxArrowDisabled) return;
    setValue(roundStep(value + step));
  };

  const onDecrease = () => {
    if (isMinArrowDisabled) return;
    setValue(roundStep(value - step));
  };

  return (
    <Arrows>
      <ArrowBox
        ref={refUp}
        isDisabled={isMaxArrowDisabled}
        onClick={onIncrease}
        data-testid="number-arrow-up"
      >
        <Icon
          name="triangleUp"
          color={
            (isMaxArrowDisabled && Color.textDisabled) ||
            (isHoveredUp && Color.white) ||
            Color.primary
          }
        />
      </ArrowBox>

      <ArrowBox
        ref={refDown}
        isDisabled={isMinArrowDisabled}
        onClick={onDecrease}
        data-testid="number-arrow-down"
      >
        <Icon
          name="triangleDown"
          color={
            (isMinArrowDisabled && Color.textDisabled) ||
            (isHoveredDown && Color.white) ||
            Color.primary
          }
        />
      </ArrowBox>
    </Arrows>
  );
};
